import { Component, OnInit, OnDestroy } from '@angular/core';
import { Room, RoomEvent, createLocalAudioTrack, RemoteTrack, RemoteTrackPublication, RemoteParticipant } from 'livekit-client';
import { AiChatWidgetService } from '../../../services/ai-chat-widget.service';
import { VideoService } from '../../../services/video.service';
import { environment } from '../../../../environments/environment';

type WidgetStatus = 'idle' | 'loading' | 'connecting' | 'connected' | 'disconnected' | 'error';

@Component({
    selector: 'app-ai-chat-widget',
    templateUrl: './ai-chat-widget.component.html',
    styleUrls: ['./ai-chat-widget.component.scss']
})
export class AiChatWidgetComponent implements OnInit, OnDestroy {
    isExpanded = false;
    isMinimized = false;
    status: WidgetStatus = 'idle';
    statusMessage = 'Ready to connect';
    room: Room | null = null;
    errorMessage = '';
    isSpeaking = false;
    participantCount = 0;
    private agentId = environment.indusLabsAgentId;

    constructor(private aiService: AiChatWidgetService, private videoService: VideoService) { }

    ngOnInit(): void {
        // Agent ID is hardcoded in environment config - no need to fetch
    }

    ngOnDestroy(): void {
        this.disconnect('onDestroy');
    }

    /**
     * Toggle widget expansion
     */
    toggleWidget(): void {
        this.isExpanded = !this.isExpanded;
        if (!this.isExpanded) {
            this.isMinimized = false;
        }
    }

    /**
     * Minimize the widget
     */
    minimize(): void {
        this.isMinimized = true;
    }

    /**
     * Restore minimized widget
     */
    restore(): void {
        this.isMinimized = false;
    }

    /**
     * Close the widget and disconnect
     */
    closeWidget(): void {
        this.disconnect('closeWidget');
        this.isExpanded = false;
        this.isMinimized = false;
    }

    /**
     * Connect to the AI agent via LiveKit
     * Per documentation: https://docs.induslabs.io/voice-agents#integrate-with-livekit
     * 
     * Flow:
     * 1. Get token and livekit_host_url from backend (which calls Indus Labs API)
     * 2. Create LiveKit Room
     * 3. Set up event listeners (Connected, Disconnected, TrackSubscribed)
     * 4. Convert URL to WebSocket protocol (wss://)
     * 5. Connect to room with token
     * 6. Enable microphone on connected
     */
    async connect(): Promise<void> {
        this.status = 'connecting';
        this.statusMessage = 'Connecting to AI agent...';
        this.errorMessage = '';

        try {
            // Step 1: Get LiveKit token from secure backend endpoint
            // Backend handles credential management - we only send agent_id
            console.log('Requesting token for agent:', this.agentId);

            const tokenResponse = await this.aiService.getLiveKitToken(this.agentId).toPromise();

            if (!tokenResponse) {
                throw new Error('No response from backend');
            }

            // Per docs: Response contains { token, livekit_host_url }
            const { token, livekit_host_url } = tokenResponse;

            if (!token || !livekit_host_url) {
                console.error('Invalid response:', tokenResponse);
                throw new Error('Missing token or livekit_host_url in response');
            }

            console.log('Token received, connecting to LiveKit...');

            // Step 2: Create LiveKit Room
            this.room = new Room();

            // Step 3: Set up event listeners per documentation
            this.room.on(RoomEvent.Connected, async () => {
                console.log('Connected to LiveKit room');
                this.participantCount = this.room?.participants.size || 0;
                console.log('Current participants:', this.participantCount);

                this.status = 'connected';
                this.statusMessage = 'Connected - Start speaking!';

                // Enable microphone to send audio to agent
                try {
                    const audioTrack = await createLocalAudioTrack();
                    await this.room!.localParticipant.publishTrack(audioTrack);
                    console.log('Microphone enabled and track published');
                } catch (err) {
                    console.error('Failed to enable microphone:', err);
                    this.errorMessage = 'Microphone access denied. Please allow microphone permissions.';
                }
            });

            // Log when participants join or leave
            this.room.on(RoomEvent.ParticipantConnected, (participant) => {
                this.participantCount = this.room?.participants.size || 0;
                console.log('Participant connected:', participant.identity, participant.metadata);
                console.log('Total participants:', this.participantCount);
            });

            this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
                this.participantCount = this.room?.participants.size || 0;
                console.log('Participant disconnected:', participant.identity);
                console.log('Total participants:', this.participantCount);
            });

            this.room.on(RoomEvent.Disconnected, () => {
                console.log('Disconnected from LiveKit room');
                this.status = 'disconnected';
                this.statusMessage = 'Disconnected';
            });

            // Subscribe to remote audio tracks (agent's voice) per documentation
            this.room.on(RoomEvent.TrackSubscribed, (
                track: RemoteTrack,
                publication: RemoteTrackPublication,
                participant: RemoteParticipant
            ) => {
                console.log(`Subscribed to track ${track.sid} from participant ${participant.identity} (kind: ${track.kind})`);

                if (track.kind === 'audio') {
                    console.log('Attaching audio track from agent');
                    const audioElement = track.attach();
                    audioElement.setAttribute('autoplay', 'true');
                    audioElement.setAttribute('playsinline', 'true');
                    document.body.appendChild(audioElement);

                    // Play audio
                    audioElement.play().catch(err => {
                        console.warn('Autoplay blocked or playback failed:', err);
                    });

                    // Visual indicator when agent is speaking
                    this.isSpeaking = true;
                    // Reset speaking indicator after a delay (this is a simple approximation)
                    // In a production app, we would listen to speaking events
                    setTimeout(() => {
                        this.isSpeaking = false;
                    }, 3000);
                }
            });

            this.room.on(RoomEvent.TrackUnsubscribed, () => {
                this.isSpeaking = false;
            });

            // Listen for data messages from the IndusLabs agent (e.g. "Play Video" action)
            // Expected payload: { "action": "play_video", "url": "<youtube-embed-url>", "title": "<video-title>" }
            this.room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
                try {
                    const decoded = new TextDecoder().decode(payload);
                    const msg = JSON.parse(decoded);
                    console.log('LiveKit DataReceived:', msg);

                    if (msg.action === 'play_video') {
                        const videoUrl = msg.url || 'https://www.youtube.com/embed/AReS4wqP0WE';
                        const videoTitle = msg.title || 'Panel Video 2026';
                        this.videoService.triggerPlay(videoUrl, videoTitle);
                    }
                } catch (e) {
                    // Payload is not JSON — safely ignore
                    console.warn('LiveKit DataReceived: non-JSON payload', e);
                }
            });

            this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
                console.log('Connection state:', state);
            });

            // Step 4: Convert URL to WebSocket protocol per documentation
            // IMPORTANT: Force WebSocket protocol (wss://) to prevent HTTP validation 404s
            let connectUrl = livekit_host_url;
            if (connectUrl.startsWith('https://')) {
                connectUrl = connectUrl.replace('https://', 'wss://');
            } else if (connectUrl.startsWith('http://')) {
                connectUrl = connectUrl.replace('http://', 'ws://');
            } else if (!connectUrl.startsWith('ws')) {
                connectUrl = 'wss://' + connectUrl;
            }

            console.log('Connecting to:', connectUrl);

            // Step 5: Connect to the room with token
            await this.room.connect(connectUrl, token, {
                autoSubscribe: true
            });

        } catch (error: any) {
            console.error('Connection failed:', error);
            this.status = 'error';
            this.statusMessage = 'Connection failed';
            this.errorMessage = error.message || 'Unable to connect to AI agent. Please try again.';
            this.disconnect(error.message || 'Connection catch');
        }
    }

    /**
     * Disconnect from the LiveKit room
     */
    disconnect(reason: string = 'manual'): void {
        console.log(`Disconnecting from room. Reason: ${reason}`);
        if (this.room) {
            this.room.disconnect();
            this.room = null;
        }
        this.status = 'idle';
        this.statusMessage = 'Ready to connect';
        this.isSpeaking = false;
        this.participantCount = 0;
    }

    /**
     * Check if connect button should be enabled
     */
    canConnect(): boolean {
        return this.status === 'idle';
    }

    /**
     * Check if disconnect button should be shown
     */
    canDisconnect(): boolean {
        return this.status === 'connected' || this.status === 'connecting';
    }
}
